import { useRef } from 'react';
import { AxiosError } from 'axios';
import isEmail from 'validator/lib/isEmail';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { LinkButton, ModalButtonWithForm } from 'components/common';
import { FormGroup, FormSection, Text } from 'components/common/form';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';

import type { CalendarApi } from '@fullcalendar/core';
import type { AxiosResponse } from 'axios';
import type { FormApi } from 'informed';
import type { CalendarEventExtended, CustomAttributes } from 'shared-types';

type FormValues = CustomAttributes & { email: string };

interface Props {
  calendarApi?: CalendarApi;
  event: CalendarEventExtended;
  setEvent?: React.Dispatch<React.SetStateAction<CalendarEventExtended>>;
}

const CalendarEventParticipantAddSelfUnauthenticated = ({
  calendarApi,
  event,
  setEvent
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const formApi = useRef<FormApi>(undefined);

  const { language, messages, theme } = useReduxState();

  const handleJoin = async (values: FormValues) => {
    const userAttributes = {};
    Object.keys(values).forEach((fieldKey) => {
      if (fieldKey.startsWith('userAttributes-')) {
        const attributeId = fieldKey.split('-')[1];
        userAttributes[attributeId] = values[fieldKey];
      }
    });

    let response: AxiosResponse;
    try {
      response = await api.post(
        `/admin/calendar-events/${event.id}/registration`,
        { email: values.email, userAttributes }
      );
    } catch (err) {
      if (!(err instanceof AxiosError)) throw err;

      const { response } = err;
      const { status, data } = response;
      if (status === 409 && data?.error === 'email_already_in_use') {
        formApi.current.setError(
          'email',
          messages.calendarEventJoinFailUserExists
        );
        return false;
      } else if (status === 409 && data?.error === 'user_already_in_event') {
        // just re-issue the success message without changing the
        // event details since the user is already in the event
        sendNotice(data?.status);
        return true;
      }
      throw err;
    }

    if (response.data.status === 'full') {
      sendNotice('full');
      return true;
    }

    const newEvent: CalendarEventExtended = {
      ...event,
      ...(response.data.status === 'queued'
        ? { numberOfParticipantsQueued: event.numberOfParticipantsQueued + 1 }
        : {
            isFull:
              event.participationLimit &&
              event.numberOfParticipantsAdded + 1 >= event.participationLimit,
            numberOfParticipantsAdded: event.numberOfParticipantsAdded + 1
          })
    };
    if (calendarApi) {
      const calendarEvent = calendarApi.getEventById(`${event.id}`);
      calendarEvent?.setExtendedProp(
        'numberOfParticipantsAdded',
        newEvent.numberOfParticipantsAdded
      );
      calendarEvent?.setExtendedProp(
        'numberOfParticipantsQueued',
        newEvent.numberOfParticipantsQueued
      );
    }
    if (setEvent) setEvent(newEvent);

    dispatch.updatePageState({ calendarEvent: newEvent });
    sendNotice(response.data.status);
    return true;
  };

  const sendNotice = (status: 'added' | 'queued' | 'full') => {
    dispatch.addNotice({
      text:
        status === 'full'
          ? messages.calendarEventJoinFull
          : status === 'queued'
          ? messages.calendarEventJoinSuccessQueue
          : messages.calendarEventJoinSuccess,
      type: status === 'full' ? 'error' : 'success'
    });
  };

  const validateEmail = (value: string) => {
    const emailValue = value?.toString().trim();
    const isValid = emailValue && isEmail(emailValue);
    return !isValid ? messages.usersInvalidEmail : undefined;
  };

  const eventAccessMessage =
    event.isFull && event.queueEnabled
      ? messages.eventCalendarJoinQueue
      : messages.eventCalendarJoinEvent;

  const eventHasStarted =
    Date.parse(event.lastJoinDate || event.startDate) <= dateNow;

  const isDisabled =
    eventHasStarted ||
    (event.isFull && !event.queueEnabled) ||
    event.isQueueFull;

  const tooltip = eventHasStarted
    ? messages.eventCalendarJoinDisabled
    : (event.isFull && !event.queueEnabled) || event.isQueueFull
    ? messages.eventCalendarIsFull
    : undefined;

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonDisabled={isDisabled}
      buttonProps={{ isRounded: true, tooltip }}
      buttonText={eventAccessMessage}
      contentStyle={{ padding: 0 }}
      initialValues={{}}
      modalTitle={eventAccessMessage}
      modalActionText={messages.commonConfirm}
      modalWidth="medium"
      onSubmit={handleJoin}
      setFormApi={(form: FormApi) => (formApi.current = form)}
    >
      {() => (
        <div className="inner">
          <div className="with-account">
            <FormSection
              helpText={messages.eventCalendarJoinEventAfterLogin}
              style={{
                display: 'flex',
                gap: 20,
                justifyContent: 'space-between'
              }}
              title={messages.authSignIn}
            >
              <LinkButton
                category="light"
                icon="sign-in-alt"
                href={`/login?next=/calendar/${event.id}`}
                text={messages.authSignIn}
              />
            </FormSection>
          </div>

          <div className="without-account">
            <div className="or">
              <div className="or-content">{messages.commonOr}</div>
            </div>

            <FormSection
              helpText={messages.eventCalendarJoinEventAsGuestLabel}
              style={{ borderTop: 0, marginTop: 0 }}
              title={messages.eventCalendarJoinEventAsGuest}
            >
              <FormGroup label={messages.commonEmail} maxWidth="300px">
                <Text
                  autoComplete="off"
                  field="email"
                  id="email"
                  required={true}
                  validate={validateEmail}
                  validateOnChange={true}
                />
              </FormGroup>

              {event.userAttributes?.map((attr) => (
                <FormGroup
                  key={attr.id}
                  label={attr.titleTranslations?.[language] || attr.title}
                  labelIsOptional={!attr.isRequired}
                >
                  <ProductUserCustomAttributeRenderFormField attribute={attr} />
                </FormGroup>
              ))}
            </FormSection>
          </div>

          <style jsx>{`
            .without-account {
              border-top: 1px solid ${theme.lightGreyDark};
              position: relative;
            }
            .or {
              position: absolute;
              top: -12px;
              left: 0;
              right: 0;
              text-align: center;
            }
            .or-content {
              background-color: ${theme.lightGrey};
              border-radius: 20px;
              color: ${theme.textColorLight};
              display: inline-block;
              font-size: 1.4rem;
              font-weight: 700;
              line-height: 1;
              padding: 4px 7px 6px;
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default CalendarEventParticipantAddSelfUnauthenticated;
